import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["children"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import PropTypes from "prop-types";
import React, { Children, useContext, useEffect, useRef } from "react";
import { useUniqueId } from "../../common/hooks";
import { mergeRefs } from "../../common/utilities";
import { DATA_ATTRIBUTE_KEY, KeyboardNavigatorContext } from "./KeyboardNavigatorUtils";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const KeyboardNavigatorItem = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    children
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const context = useContext(KeyboardNavigatorContext);
  const {
    addFocusableElement,
    removeFocusableElement,
    shouldSetTabIndex,
    disableTabIndex,
    shouldFocusElement,
    shouldFocusActiveElementOnMount,
    getActiveElementIndex,
    prevActiveElementIndex,
    preventFocus
  } = context;
  const childProps = Children.only(children).props;
  const keyboardNavigatorItemUniqueId = useUniqueId({
    id: childProps.id,
    prefix: "KeyboardNavigatorItem"
  });
  const isMounted = useRef(false); // Add the component Id to the parent list so it can be tracked down

  !isMounted.current && addFocusableElement(keyboardNavigatorItemUniqueId, childProps.disabled); // Track activeElementIndex to later compare and focus item only when activeElementIndex changes

  const activeElementIndex = getActiveElementIndex();
  const domNode = useRef();
  const combineRefs = mergeRefs([domNode, ref]);
  useEffect(() => {
    const currentDomNode = domNode && domNode.current; // Validates if this item should be focused or not on mount.

    if (currentDomNode && (currentDomNode.tabIndex === 0 || currentDomNode.tabIndex === undefined) && // eslint-disable-line no-undefined
    shouldFocusActiveElementOnMount(keyboardNavigatorItemUniqueId) && currentDomNode.focus && !preventFocus) {
      currentDomNode.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Below effect should do following things:
   *  - if any of the `NavigatorItem` is disabled then it should
   *    remove that particular item index from the activeIndexes array.
   *  - Else is for, if an item is enabled programmatically after it is mounted
   *    then we need to add the enabled item's index into activeIndexes array list.
   */

  useEffect(() => {
    if (childProps.disabled) {
      removeFocusableElement(keyboardNavigatorItemUniqueId);
    } else {
      addFocusableElement(keyboardNavigatorItemUniqueId);
    }
  }, [addFocusableElement, childProps.disabled, keyboardNavigatorItemUniqueId, removeFocusableElement]);
  useEffect(() => {
    const currentNode = domNode && domNode.current;
    /**
     *  Validates if this item should be focused or not.
     *
     *  Also prevents execution of below lines on mount to
     *  honor `focusActiveElementOnMount` prop.
     */

    if (activeElementIndex !== prevActiveElementIndex && currentNode.focus && (currentNode.tabIndex === 0 || currentNode.tabIndex === undefined) && // eslint-disable-line no-undefined
    shouldFocusElement(keyboardNavigatorItemUniqueId) && isMounted && isMounted.current && !preventFocus) {
      currentNode.focus();
    }

    currentNode && (isMounted.current = true);
  });

  const renderChild = () => {
    return React.Children.map(children, element => {
      const extensions = _objectSpread({
        [DATA_ATTRIBUTE_KEY]: keyboardNavigatorItemUniqueId,
        ref: combineRefs
      }, otherProps); // For Global Navigator


      if (!disableTabIndex) {
        extensions.tabIndex = shouldSetTabIndex(keyboardNavigatorItemUniqueId) ? 0 : -1;
      }

      return /*#__PURE__*/React.cloneElement(element, extensions);
    });
  };

  return _jsx(React.Fragment, {
    children: renderChild()
  });
});
KeyboardNavigatorItem.propTypes = process.env.NODE_ENV !== "production" ? {
  children: PropTypes.element.isRequired
} : {};
KeyboardNavigatorItem.displayName = "KeyboardNavigatorItem";
export default KeyboardNavigatorItem;